import { createClient } from 'contentful';

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
});

if (!process.env.REACT_APP_CONTENTFUL_SPACE_ID || !process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN) {
  console.warn('Contentful environment variables not found');
}


// Contentful content only changes at build time, so navigating between pages
// should never refetch what this session already holds — that refetch is what
// put a spinner on screen every time you moved. Caching the promise rather than
// the result also collapses concurrent calls when two components mount at once.
const memo = new Map();
const cached = (key, run) => {
  if (!memo.has(key)) {
    memo.set(key, run().catch((err) => { memo.delete(key); throw err; }));
  }
  return memo.get(key);
};

export const fetchEntries = () => cached('entries', async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'artwork',
      select: 'fields.title,fields.slug,fields.image,fields.tags',
      include: 2
    });
    return entries.items;
  } catch (err) {
    console.error('Failed to fetch entries from Contentful:', err);
    throw new Error('Could not load artworks');
  }
});

const ensureHttps = (url) => {
  if (!url) return null;
  return url.startsWith('//') ? `https:${url}` : url;
};

// Some video titles were saved into Contentful already HTML-escaped
// ("Switzerland&#39;s"), which React then renders literally. Decode on read.
const decodeEntities = (str) => {
  if (typeof str !== 'string' || !str.includes('&') || typeof document === 'undefined') return str;
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
};

export const fetchVideos = () => cached('videos', async () => {
  try {
    const entries = await client.getEntries({ content_type: 'video', include: 2, order: '-fields.order' });
    return entries.items.map((item) => ({
      id: item.fields.youtubeId || item.sys.id,
      title: decodeEntities(item.fields.title),
      videoUrl: ensureHttps(item.fields.videoFile?.fields?.file?.url || item.fields.videoUrl),
      thumbnail: ensureHttps(item.fields.thumbnail?.fields?.file?.url),
      publishedAt: item.sys.createdAt,
      order: item.fields.order || 0,
    }));
  } catch (err) {
    console.warn('Failed to fetch videos from Contentful:', err);
    return [];
  }
});

export const fetchVideoShorts = () => cached('videoShorts', async () => {
  try {
    const entries = await client.getEntries({ content_type: 'videoShort', include: 2, order: '-fields.order' });
    return entries.items.map((item) => ({
      id: item.fields.youtubeId || item.sys.id,
      title: decodeEntities(item.fields.title),
      videoUrl: ensureHttps(item.fields.video?.fields?.file?.url || item.fields.videoFile?.fields?.file?.url || item.fields.videoUrl),
      thumbnail: ensureHttps(item.fields.thumbnail?.fields?.file?.url),
      publishedAt: item.sys.createdAt,
      order: item.fields.order || 0,
    }));
  } catch (err) {
    console.warn('Failed to fetch video shorts from Contentful:', err);
    return [];
  }
});

export const fetchEntry = (slug) => cached(`entry:${slug}`, async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'artwork',
      'fields.slug': slug,
    });
    return entries.items[0];
  } catch (err) {
    console.error('Failed to fetch entry from Contentful:', err);
    throw new Error('Could not load artwork');
  }
});

export const fetchBlogPosts = () => cached('blogPosts', async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'blogPost',
      order: '-fields.publishedAt',
      include: 2,
    });
    return entries.items;
  } catch (err) {
    console.warn('Failed to fetch blog posts from Contentful:', err);
    return [];
  }
});

export const fetchBlogPost = (slug) => cached(`blogPost:${slug}`, async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'blogPost',
      'fields.slug': slug,
      include: 2,
      limit: 1,
    });
    return entries.items[0];
  } catch (err) {
    console.error('Failed to fetch blog post from Contentful:', err);
    throw new Error('Could not load blog post');
  }
});
